import React, { useState, useContext } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Container
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuthenticator } from '@aws-amplify/ui-react';
import SearchAppBar from './search/SearchAppBar';
import FilterDrawer from './search/FilterDrawer';
import SlugSearchResults from './people/SlugSearchResults';
import UnifiedSettingsDialog from './dialogs/UnifiedSettingsDialog';
import { useSettings } from '../contexts/settingsContext';
import { useSearch } from '../contexts/searchContext';
import { CognitoUserContext } from '../contexts/cognitoUserContext';
import { HistoryDrawerProvider } from '../contexts/historyDrawerContext';
import { useSearchPeople } from './search/useSearchPeople';
import {
  setLocalStorageEnumMap,
  clearLocalStorageEnumMap,
} from '../services/user/browserService';

/**
 * ModeratorApp - Minimal interface for moderators
 * Only search functionality to find people for editing characteristics
 */
export default function ModeratorApp() {
  const { signOut } = useAuthenticator();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const settings = useSettings();

  const { cognitoUser } = useContext(CognitoUserContext);

  // Use unified search context
  const {
    datasetMap,
    selectedDataset,
    enumMap,
    setEnumMap,
    searchString,
    setFilteredPeople,
    setMatchSource
  } = useSearch();

  const { searchPeople } = useSearchPeople(setFilteredPeople, setMatchSource);

  const myarr = Array.from(enumMap);

  const handleSignOut = () => signOut();

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    const tempMap = new Map(enumMap);
    tempMap.set('Dataset', selectedDataset);
    setLocalStorageEnumMap(tempMap);
    searchPeople(tempMap, searchString);
    setDrawerOpen(false);
  };

  const handleDrawerQuit = () => {
    setDrawerOpen(false);
  };

  const handleDrawerClear = () => {
    const clearMap = new Map();
    datasetMap.set(selectedDataset, clearMap);
    setEnumMap(clearMap);
    clearLocalStorageEnumMap();
  };

  return (
    <HistoryDrawerProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* App Bar */}
        <AppBar position="static" sx={{ bgcolor: '#2d3e50' }}>
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Moderator Panel
            </Typography>

            <IconButton
              color="inherit"
              onClick={() => setSettingsOpen(true)}
              title="Settings"
            >
              <SettingsIcon />
            </IconButton>

            <IconButton
              color="inherit"
              onClick={handleSignOut}
              title="Sign Out"
            >
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Search Interface */}
        <Box sx={{ flexGrow: 1 }}>
          <SearchAppBar
            drawerOpen={drawerOpen}
            onHistoryOpen={() => {}} // No history for moderators
            onCameraOpen={() => {}} // No camera for moderators
            onStealthOpen={() => {}} // No stealth for moderators
            onDrawerOpen={handleDrawerOpen}
            onSettingsOpen={() => setSettingsOpen(true)}
            onAdminOpen={() => {}} // No admin access
            cognitoUser={cognitoUser}
            stealthMode={false}
            myarr={myarr}
            isAdminMode={true} // Hide camera/history buttons
          />

          <FilterDrawer
            open={drawerOpen}
            onClose={handleDrawerQuit}
            onApply={handleDrawerClose}
            onClear={handleDrawerClear}
            enumMap={enumMap}
          />

          <Container maxWidth="lg" sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
              Search for people to edit their characteristics. Click on a person card to open the editor.
            </Typography>
            <SlugSearchResults />
          </Container>
        </Box>

        {/* Settings Dialog */}
        <UnifiedSettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          // History settings (disabled for moderators)
          deleteConfirmation={settings.deleteConfirmation}
          onDeleteConfirmationChange={settings.handleDeleteConfirmationChange}
          autoEnableVoice={settings.autoEnableVoice}
          onAutoEnableVoiceChange={settings.setAutoEnableVoice}
          stealthMode={false}
          onStealthModeChange={() => {}}
          // Camera settings (disabled for moderators)
          removeBg={settings.removeBg}
          setRemoveBg={settings.setRemoveBg}
          sliderValue={settings.sliderValue}
          setSliderValue={settings.setSliderValue}
          maxFacesValue={settings.maxFacesValue}
          setMaxFacesValue={settings.setMaxFacesValue}
          showBoundingBoxes={settings.showBoundingBoxes}
          setShowBoundingBoxes={settings.setShowBoundingBoxes}
          immediateFaceMatch={settings.immediateFaceMatch}
          setImmediateFaceMatch={settings.setImmediateFaceMatch}
          updateCharacteristicsOnMatch={settings.updateCharacteristicsOnMatch}
          setUpdateCharacteristicsOnMatch={settings.setUpdateCharacteristicsOnMatch}
          compressionQuality={settings.compressionQuality}
          setCompressionQuality={settings.setCompressionQuality}
          autoAdjustCompression={settings.autoAdjustCompression}
          setAutoAdjustCompression={settings.setAutoAdjustCompression}
          autoCreateEventOnAIMatch={settings.autoCreateEventOnAIMatch}
          setAutoCreateEventOnAIMatch={settings.setAutoCreateEventOnAIMatch}
          locationHighAccuracy={settings.locationHighAccuracy}
          setLocationHighAccuracy={settings.setLocationHighAccuracy}
          locationAcquisitionTimeout={settings.locationAcquisitionTimeout}
          setLocationAcquisitionTimeout={settings.setLocationAcquisitionTimeout}
          locationCacheMaxAge={settings.locationCacheMaxAge}
          setLocationCacheMaxAge={settings.setLocationCacheMaxAge}
          aggregatorEventLimit={settings.aggregatorEventLimit}
          setAggregatorEventLimit={settings.setAggregatorEventLimit}
        />
      </Box>
    </HistoryDrawerProvider>
  );
}
